// Animated ocean background — fixed canvas behind #app.
// Procedural wave drawing (no assets), 2 drifting ship silhouettes, ambient
// fog band on the horizon. Caps at ~30 fps to be mobile-friendly. Honors
// prefers-reduced-motion (renders once, no animation loop).
let canvas = null;
let ctx = null;
let raf = 0;
let lastFrame = 0;
let started = false;
let ships = [];
export function mountOceanBackground() {
    if (started)
        return;
    started = true;
    canvas = document.createElement('canvas');
    canvas.id = 'ocean-bg';
    // Style is owned by CSS now — leave only the things that must be inline
    // (zIndex / pointer-events) so user-stylesheet/CSP can't override them.
    canvas.style.zIndex = '-1';
    canvas.style.pointerEvents = 'none';
    document.body.prepend(canvas);
    document.documentElement.classList.add('has-ocean-bg');
    ctx = canvas.getContext('2d');
    if (!ctx)
        return;
    resize();
    window.addEventListener('resize', resize, { passive: true });
    // Canvas now lives in body coordinate space (position:absolute), same
    // frame as the UI — no visualViewport pinning needed. They move together
    // by construction.
    // Three ships at progressively closer distances. Distance drives every
    // visual cue — size, position above the horizon, opacity, speed — so the
    // perspective reads correctly: far ships are small/slow/faint near the
    // horizon, near ships are big/fast/sharp closer to the foreground.
    ships = [
        { x: 0.15, distance: 0.10, speed: 4, flip: false, bobPhase: 0.0 },
        { x: 0.62, distance: 0.45, speed: 9, flip: true, bobPhase: 1.7 },
        { x: 0.85, distance: 0.85, speed: 14, flip: false, bobPhase: 3.1 },
    ];
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
        drawFrame(0);
        return;
    }
    lastFrame = performance.now();
    raf = requestAnimationFrame(loop);
}
export function unmountOceanBackground() {
    if (raf)
        cancelAnimationFrame(raf);
    raf = 0;
    if (canvas)
        canvas.remove();
    canvas = null;
    ctx = null;
    started = false;
    window.removeEventListener('resize', resize);
}
let lastSizeW = 0;
let lastSizeH = 0;
function resize() {
    if (!canvas)
        return;
    // Read the canvas's actual rendered box (driven by CSS 100lvh). This is
    // stable across iOS address-bar collapse because lvh = largest viewport
    // height — it doesn't shrink when the toolbar reappears.
    const rect = canvas.getBoundingClientRect();
    const w = Math.round(rect.width) || window.innerWidth;
    const h = Math.round(rect.height) || window.innerHeight;
    const widthChanged = Math.abs(w - lastSizeW) > 1;
    const heightChangedSignificantly = Math.abs(h - lastSizeH) > 8;
    if (lastSizeW !== 0 && !widthChanged && !heightChangedSignificantly)
        return;
    lastSizeW = w;
    lastSizeH = h;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    if (ctx)
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
function loop(now) {
    raf = requestAnimationFrame(loop);
    // Cap to ~30fps
    if (now - lastFrame < 33)
        return;
    const dt = Math.min(0.05, (now - lastFrame) / 1000);
    lastFrame = now;
    drawFrame(dt);
}
function drawFrame(dt) {
    if (!ctx || !canvas)
        return;
    // Use the LOCKED canvas size (driven by CSS 100lvh). Locked across iOS
    // address-bar shows/hides so the horizon and ships stay put.
    const W = lastSizeW || window.innerWidth;
    const H = lastSizeH || window.innerHeight;
    const horizonY = H * 0.55;
    // Sky gradient (dawn → midnight)
    const sky = ctx.createLinearGradient(0, 0, 0, horizonY);
    sky.addColorStop(0, '#0a121f');
    sky.addColorStop(0.55, '#172536');
    sky.addColorStop(1, '#1f3548');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, W, horizonY);
    // Horizon fog band
    const fog = ctx.createLinearGradient(0, horizonY - 60, 0, horizonY + 30);
    fog.addColorStop(0, 'rgba(220, 200, 160, 0)');
    fog.addColorStop(0.5, 'rgba(220, 200, 160, 0.10)');
    fog.addColorStop(1, 'rgba(20, 30, 45, 0.85)');
    ctx.fillStyle = fog;
    ctx.fillRect(0, horizonY - 60, W, 90);
    // Sea gradient (dark teal → dark)
    const sea = ctx.createLinearGradient(0, horizonY, 0, H);
    sea.addColorStop(0, '#1a3043');
    sea.addColorStop(1, '#0a1a26');
    ctx.fillStyle = sea;
    ctx.fillRect(0, horizonY, W, H - horizonY);
    // Subtle moon glow
    const moonX = W * 0.78;
    const moonY = H * 0.18;
    const moon = ctx.createRadialGradient(moonX, moonY, 4, moonX, moonY, 90);
    moon.addColorStop(0, 'rgba(245, 230, 195, 0.55)');
    moon.addColorStop(0.4, 'rgba(245, 230, 195, 0.15)');
    moon.addColorStop(1, 'rgba(245, 230, 195, 0)');
    ctx.fillStyle = moon;
    ctx.fillRect(moonX - 100, moonY - 100, 200, 200);
    ctx.fillStyle = '#f5e6c3';
    ctx.beginPath();
    ctx.arc(moonX, moonY, 18, 0, Math.PI * 2);
    ctx.fill();
    // Sea floor — a few subtle horizon-line ripples to suggest distant water
    // before the wave bands start in earnest. Adds depth.
    ctx.fillStyle = 'rgba(120, 160, 180, 0.18)';
    for (let i = 0; i < 18; i++) {
        const ry = horizonY + 2 + i * 1.6;
        const rw = W * (0.6 - i * 0.025);
        if (rw <= 0)
            break;
        ctx.fillRect((W - rw) / 2, ry, rw, 1);
    }
    const time = lastFrame / 1000;
    // Draw FAR ships first (back-to-front), interleaved with wave bands of
    // matching depth so that closer waves can occlude distant ships' hulls.
    // Sort by distance ascending: 0 = far → 1 = near.
    const sorted = [...ships].sort((a, b) => a.distance - b.distance);
    // Wave bands: more layers, higher amplitude, foam line on the closest.
    // Each band represents a "depth slice" of water.
    const bands = [
        { offset: 6, amp: 4, speed: 0.4, step: 18, color: 'rgba(120, 170, 195, 0.35)', foam: false },
        { offset: 20, amp: 8, speed: 0.55, step: 22, color: 'rgba(80, 130, 160, 0.42)', foam: false },
        { offset: 48, amp: 14, speed: 0.8, step: 28, color: 'rgba(60, 105, 135, 0.55)', foam: true },
        { offset: 95, amp: 22, speed: 1.15, step: 34, color: 'rgba(38, 78, 108, 0.65)', foam: true },
        { offset: 165, amp: 32, speed: 1.55, step: 42, color: 'rgba(22, 50, 75, 0.78)', foam: true },
        { offset: 250, amp: 44, speed: 2.0, step: 50, color: 'rgba(12, 30, 50, 0.88)', foam: false },
    ];
    // Interleave ships and bands — each ship sits between two bands so closer
    // bands occlude distant ships' lower halves naturally.
    let shipIdx = 0;
    for (let i = 0; i < bands.length; i++) {
        const b = bands[i];
        // Draw all ships whose distance maps to this band slot before drawing the
        // band itself, so this band can paint over them.
        while (shipIdx < sorted.length && sorted[shipIdx].distance <= (i + 1) / bands.length) {
            const s = sorted[shipIdx++];
            // Ships sit ON the wave they're behind: closer ships sit on a deeper
            // (lower) band. Distance 0 → at the horizon; distance 1 → on the
            // foreground swell. yFromHorizon is always >= 0 so a ship can never
            // float above the horizon line.
            const yFromHorizon = Math.max(2, b.offset - 6);
            s.x = s.x + (s.speed * dt) / W;
            if (s.x > 1.25)
                s.x = -0.25;
            if (s.x < -0.25)
                s.x = 1.25;
            const screenX = W * s.x;
            const bob = Math.sin(time * 1.4 + s.bobPhase) * (1 + s.distance * 2);
            drawShip(ctx, screenX, horizonY + yFromHorizon + bob, s.distance, s.flip);
        }
        drawWaveBand(ctx, horizonY + b.offset, b.amp, time * b.speed, b.step, b.color, b.foam);
    }
    // Any ships further than the last band slot
    while (shipIdx < sorted.length) {
        const s = sorted[shipIdx++];
        s.x += (s.speed * dt) / W;
        if (s.x > 1.25)
            s.x = -0.25;
        if (s.x < -0.25)
            s.x = 1.25;
        drawShip(ctx, W * s.x, horizonY + 280, s.distance, s.flip);
    }
}
function drawWaveBand(ctx, baseY, amp, phase, step, fill, foam) {
    const W = lastSizeW || window.innerWidth;
    const H = lastSizeH || window.innerHeight;
    // Build the wave as 4 stacked sin frequencies for organic motion. Lower
    // frequency = swell, higher = ripple. Each phase moves at a different
    // rate so the surface never repeats cleanly.
    const ys = [];
    for (let x = 0; x <= W; x += step) {
        const u = x / W;
        const y = baseY
            + Math.sin(u * Math.PI * 2.0 + phase * 0.7) * amp
            + Math.sin(u * Math.PI * 5.5 + phase * 1.4) * amp * 0.45
            + Math.sin(u * Math.PI * 13.0 + phase * 2.6) * amp * 0.18
            + Math.sin(u * Math.PI * 31.0 + phase * 4.0) * amp * 0.07;
        ys.push(y);
    }
    // Fill the water below the wave line.
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(0, H);
    ctx.lineTo(0, ys[0]);
    let xi = 0;
    for (let x = 0; x <= W; x += step) {
        ctx.lineTo(x, ys[xi++]);
    }
    ctx.lineTo(W, H);
    ctx.closePath();
    ctx.fill();
    // Foam: thin lighter line tracing the wave crest. Only on mid-distance
    // bands where it reads as whitecaps without becoming noisy.
    if (foam) {
        ctx.strokeStyle = 'rgba(230, 240, 250, 0.28)';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        xi = 0;
        for (let x = 0; x <= W; x += step) {
            const y = ys[xi++];
            if (xi === 1)
                ctx.moveTo(x, y);
            else
                ctx.lineTo(x, y);
        }
        ctx.stroke();
    }
}
function drawShip(ctx, x, y, distance, flip) {
    // Distance-based perspective:
    //   - size scales from ~0.35x (far) to ~1.4x (near)
    //   - opacity from 0.45 (atmospheric perspective haze) to 1.0 (sharp)
    //   - color shifts toward sky tint when far (looks foggy/atmospheric)
    const scale = 0.35 + distance * 1.05;
    const alpha = 0.45 + distance * 0.55;
    // Far ships pick up sky color (atmospheric haze), near ships are dark/sharp.
    const hullR = Math.round(10 + (1 - distance) * 60);
    const hullG = Math.round(20 + (1 - distance) * 70);
    const hullB = Math.round(35 + (1 - distance) * 80);
    ctx.save();
    ctx.translate(x, y);
    if (flip)
        ctx.scale(-1, 1);
    ctx.scale(scale, scale);
    ctx.fillStyle = `rgba(${hullR}, ${hullG}, ${hullB}, ${alpha})`;
    // Hull — slight curve, deeper on the near ships
    ctx.beginPath();
    ctx.moveTo(-34, 0);
    ctx.quadraticCurveTo(0, 4, 34, 0);
    ctx.lineTo(28, 11);
    ctx.lineTo(-28, 11);
    ctx.closePath();
    ctx.fill();
    // Hull stripe
    ctx.fillStyle = `rgba(${Math.min(255, hullR + 20)}, ${Math.min(255, hullG + 25)}, ${Math.min(255, hullB + 30)}, ${alpha * 0.7})`;
    ctx.fillRect(-30, 4, 60, 1.5);
    // Two masts + sails — taller on the near ship for distinctness
    const mastH = 30 + distance * 8;
    ctx.fillStyle = `rgba(${hullR}, ${hullG}, ${hullB}, ${alpha})`;
    ctx.fillRect(-12, -mastH, 1.6, mastH);
    ctx.fillRect(10, -mastH * 0.85, 1.6, mastH * 0.85);
    // Forward sail
    ctx.fillStyle = `rgba(232, 213, 168, ${alpha * 0.85})`;
    ctx.beginPath();
    ctx.moveTo(-12, -mastH + 2);
    ctx.lineTo(2, -mastH * 0.55);
    ctx.lineTo(-12, -mastH * 0.4);
    ctx.closePath();
    ctx.fill();
    // Aft sail
    ctx.beginPath();
    ctx.moveTo(10, -mastH * 0.85 + 2);
    ctx.lineTo(22, -mastH * 0.4);
    ctx.lineTo(10, -mastH * 0.3);
    ctx.closePath();
    ctx.fill();
    // Mainsail middle
    ctx.beginPath();
    ctx.moveTo(-2, -mastH + 2);
    ctx.lineTo(8, -mastH * 0.45);
    ctx.lineTo(-2, -mastH * 0.35);
    ctx.closePath();
    ctx.fill();
    // Flag — only on near/mid ships (far ships too small to read it)
    if (distance > 0.25) {
        ctx.fillStyle = `rgba(168, 50, 50, ${alpha * 0.9})`;
        ctx.beginPath();
        ctx.moveTo(-12, -mastH);
        ctx.lineTo(-2, -mastH + 3);
        ctx.lineTo(-12, -mastH + 6);
        ctx.closePath();
        ctx.fill();
    }
    ctx.restore();
}
